import { useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { api, ApiError } from "../../api";
import { ErrorNote, formatDate, HealthBadge, Loading, timeAgo, useApi } from "../../components/ui";
import { HealthTable, ProjectList, type ProjectSummary } from "./Dashboard";
import { usePickers } from "./shared";

type HealthRow = Parameters<typeof HealthTable>[0]["customers"][number];

export function StaffCustomers() {
  const { data, error, loading, reload } = useApi<{ orgs: Array<{ id: string; name: string; archived: number; domains: string | null; users: number; apps: number; health: HealthRow | null }> }>("/staff/orgs");
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("");
  const active = (data?.orgs ?? []).filter((o) => !o.archived);
  const health = active
    .map((o) => o.health)
    .filter((h): h is HealthRow => Boolean(h))
    .filter((h) => (!band || h.band === band) && h.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.score - b.score);
  const archived = (data?.orgs ?? []).filter((o) => o.archived);
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Customers</h1>
          <p className="muted small" style={{ margin: "4px 0 0" }}>
            {active.length} active · sorted by health, worst first
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setAdding(true)} data-tour="add-customer">Add customer</button>
      </div>
      {adding && <NewOrg onDone={() => (setAdding(false), reload())} />}
      <div className="filters">
        <label className="sr-only" htmlFor="cq">Search customers</label>
        <input id="cq" type="search" placeholder="Search customers" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="sr-only" htmlFor="cband">Health</label>
        <select id="cband" value={band} onChange={(e) => setBand(e.target.value)}>
          <option value="">Health: any</option>
          <option value="at_risk">At risk</option>
          <option value="watch">Watch</option>
          <option value="healthy">Healthy</option>
        </select>
      </div>
      <ErrorNote message={error} />
      {loading && !data ? <Loading /> : (
        <div className="card">
          <HealthTable customers={health} />
        </div>
      )}
      {archived.length > 0 && (
        <details style={{ marginTop: 16 }}>
          <summary className="small" style={{ cursor: "pointer" }}>{archived.length} archived</summary>
          <ul className="list-plain">
            {archived.map((o) => <li key={o.id}><Link href={`/staff/customers/${o.id}`}>{o.name}</Link></li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

function NewOrg({ onDone }: { onDone: () => void }) {
  const [, navigate] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const domains = String(f.get("domains") ?? "").split(/[\s,]+/).map((d) => d.trim().toLowerCase().replace(/^@/, "")).filter(Boolean);
    try {
      const r = await api<{ id: string }>("/staff/orgs", { method: "POST", json: { name: f.get("name"), tier: f.get("tier"), domains } });
      onDone();
      navigate(`/staff/customers/${r.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create the customer.");
    }
  };
  return (
    <form className="card stack" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h2>Add customer</h2>
      <div className="grid grid-3">
        <div className="field">
          <label htmlFor="o-name">Company name</label>
          <input id="o-name" name="name" type="text" required maxLength={120} />
        </div>
        <div className="field">
          <label htmlFor="o-domains">Email domains</label>
          <input id="o-domains" name="domains" type="text" placeholder="acme.com, acme.co.uk" />
          <span className="hint">Anyone at these domains can sign in.</span>
        </div>
        <div className="field">
          <label htmlFor="o-tier">Support tier</label>
          <select id="o-tier" name="tier">
            <option value="standard">Standard</option>
            <option value="priority">Priority</option>
          </select>
        </div>
      </div>
      {error && <p className="error-text" role="alert">{error}</p>}
      <div className="row">
        <button className="btn btn-primary">Create</button>
        <button type="button" className="btn" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

interface OrgDetail {
  org: { id: string; name: string; tier: string; notes: string; archived: number; account_owner_id: string | null; created_at: string };
  domains: string[];
  users: Array<{ id: string; email: string; name: string; disabled: number; last_login_at: string | null }>;
  apps: Array<{ id: string; name: string; github_repo: string | null; active_keys: number }>;
  health: HealthRow | null;
  projects: ProjectSummary[];
}

const SYSTEM_NAMES: Record<string, string> = { crumb: "Crumb" };
const ONBOARDING_LABEL: Record<string, string> = { created: "Invited, nobody has signed in yet", onboarded: "Onboarded", archived: "Archived" };

export function StaffCustomer({ id }: { id: string }) {
  const { data, error, loading, reload } = useApi<OrgDetail>(`/staff/orgs/${id}`);
  const integration = useApi<{
    customer: { externalRefs: Array<{ system: string; externalId: string }>; onboarding: { status: string; onboardedAt: string | null; contactsSignedIn: number; contacts: number } };
  }>(`/customers/${id}`);
  const pickers = usePickers();
  const [saveError, setSaveError] = useState<string | null>(null);
  if (loading && !data) return <div className="page"><Loading /></div>;
  if (!data) return <div className="page"><ErrorNote message={error} /></div>;
  const { org } = data;
  const patch = async (change: Record<string, unknown>) => {
    setSaveError(null);
    try {
      await api(`/staff/orgs/${id}`, { method: "PATCH", json: change });
      reload();
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : "Couldn't save.");
    }
  };
  return (
    <div className="page stack">
      <div>
        <p className="eyebrow"><Link href="/staff/customers">Customers</Link> /</p>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h1>{org.name}{org.archived ? " (archived)" : ""}</h1>
          <div className="row">
            {data.health && <HealthBadge band={data.health.band} score={data.health.score} />}
            <Link href={`/staff/queue?org=${org.id}&view=all`} className="btn btn-small">Open items</Link>
          </div>
        </div>
        {data.health && data.health.reasons.length > 0 && <p className="small muted">{data.health.reasons.join("; ")}</p>}
        {integration.data && (
          <p className="small" style={{ margin: "6px 0 0" }} data-tour="customer-integration">
            <span className={`chip ${integration.data.customer.onboarding.status === "onboarded" ? "chip-resolved" : integration.data.customer.onboarding.status === "archived" ? "chip-closed" : "chip-open"}`}>
              {ONBOARDING_LABEL[integration.data.customer.onboarding.status] ?? integration.data.customer.onboarding.status}
            </span>{" "}
            <span className="muted">
              {integration.data.customer.onboarding.onboardedAt ? `since ${formatDate(integration.data.customer.onboarding.onboardedAt)} · ` : ""}
              {integration.data.customer.onboarding.contactsSignedIn} of {integration.data.customer.onboarding.contacts} contacts have signed in
            </span>
            {integration.data.customer.externalRefs.map((r) => (
              <span key={r.system} className="muted">
                {" "}
                · Managed in <strong>{SYSTEM_NAMES[r.system] ?? r.system}</strong> ({r.externalId}): edit the name, domains, tier and contacts there; changes here are overwritten on its next sync.
              </span>
            ))}
          </p>
        )}
      </div>
      <ErrorNote message={saveError} />
      <div className="grid grid-2">
        <section className="card stack">
          <h2>Account</h2>
          <div className="field">
            <label htmlFor="c-name">Name</label>
            <input id="c-name" type="text" defaultValue={org.name} onBlur={(e) => e.target.value !== org.name && patch({ name: e.target.value })} />
          </div>
          <div className="grid grid-2">
            <div className="field">
              <label htmlFor="c-tier">Support tier</label>
              <select id="c-tier" value={org.tier} onChange={(e) => patch({ tier: e.target.value })}>
                <option value="standard">Standard</option>
                <option value="priority">Priority</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="c-owner">Account owner</label>
              <select id="c-owner" value={org.account_owner_id ?? ""} onChange={(e) => patch({ accountOwnerId: e.target.value || null })}>
                <option value="">None</option>
                {pickers.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="c-domains">Email domains</label>
            <input
              id="c-domains"
              type="text"
              defaultValue={data.domains.join(", ")}
              onBlur={(e) => {
                const domains = e.target.value.split(/[\s,]+/).map((d) => d.trim().toLowerCase().replace(/^@/, "")).filter(Boolean);
                if (domains.join(",") !== data.domains.join(",")) patch({ domains });
              }}
            />
            <span className="hint">Anyone at these domains can sign in and joins this customer.</span>
          </div>
          <div className="field">
            <label htmlFor="c-notes">Notes (staff only)</label>
            <textarea id="c-notes" defaultValue={org.notes} onBlur={(e) => e.target.value !== org.notes && patch({ notes: e.target.value })} />
          </div>
          <div>
            <button className="btn btn-small" onClick={() => patch({ archived: !org.archived })}>{org.archived ? "Unarchive" : "Archive customer"}</button>
          </div>
        </section>
        <section className="card stack">
          <h2>People</h2>
          <Users orgId={org.id} users={data.users} onChange={reload} />
        </section>
      </div>
      <div className="grid grid-2">
        <section className="card stack" data-tour="org-apps">
          <h2>Apps &amp; feedback keys</h2>
          <Apps orgId={org.id} apps={data.apps} onChange={reload} />
        </section>
        <section className="card">
          <div className="card-head">
            <h2>Projects</h2>
            <Link href="/staff/projects">Manage</Link>
          </div>
          <ProjectList projects={data.projects} />
        </section>
      </div>
    </div>
  );
}

function Users({ orgId, users, onChange }: { orgId: string; users: OrgDetail["users"]; onChange: () => void }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api(`/staff/orgs/${orgId}/users`, { method: "POST", json: { email } });
      setEmail("");
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't add them.");
    }
  };
  return (
    <>
      {users.length === 0 ? <p className="muted small">Nobody has signed in yet.</p> : (
        <ul className="list-plain">
          {users.map((u) => (
            <li key={u.id} className="row" style={{ justifyContent: "space-between" }}>
              <span>
                {u.name ? `${u.name} · ` : ""}{u.email}
                <span className="small muted"> · {u.last_login_at ? `signed in ${timeAgo(u.last_login_at)}` : "never signed in"}</span>
                {u.disabled ? <strong className="small"> · disabled</strong> : null}
              </span>
              <button className="btn btn-small" onClick={() => api(`/staff/users/${u.id}`, { method: "PATCH", json: { disabled: !u.disabled } }).then(onChange)}>
                {u.disabled ? "Enable" : "Disable"}
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="row" onSubmit={add} style={{ alignItems: "flex-end" }} data-tour="org-people">
        <div className="field" style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor="u-email">Invite someone</label>
          <input id="u-email" type="email" required placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <button className="btn">Invite</button>
      </form>
      <p className="hint small" style={{ margin: 0 }}>For people outside the customer's email domains. They get an email with a sign-in link.</p>
      {error && <p className="error-text" role="alert">{error}</p>}
    </>
  );
}

function Apps({ orgId, apps, onChange }: { orgId: string; apps: OrgDetail["apps"]; onChange: () => void }) {
  const [name, setName] = useState("");
  const [repo, setRepo] = useState("");
  const [newKey, setNewKey] = useState<{ key: string; endpoint: string } | null>(null);
  const [keysFor, setKeysFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api(`/staff/orgs/${orgId}/apps`, { method: "POST", json: { name, githubRepo: repo || null } });
      setName("");
      setRepo("");
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't add the app.");
    }
  };
  const createKey = async (appId: string) => {
    const r = await api<{ key: string; endpoint: string }>(`/staff/apps/${appId}/keys`, { method: "POST", json: { label: "" } });
    setNewKey(r);
    onChange();
  };
  return (
    <>
      <p className="small muted" style={{ margin: 0 }}>
        Each DL app this customer runs. Give the app a key so its feedback module can push in-app requests here; link its GitHub repo so issues roll up to this customer.
      </p>
      {newKey && (
        <div className="notice stack" role="status">
          <strong>Copy this key into the app's feedback settings now. It won't be shown again.</strong>
          <div className="keybox">{newKey.key}</div>
          <span className="small">Endpoint: <code className="mono">POST {newKey.endpoint}</code></span>
          <div className="row">
            <button className="btn btn-small" onClick={() => navigator.clipboard.writeText(newKey.key)}>Copy key</button>
            <button className="btn btn-small" onClick={() => setNewKey(null)}>Done</button>
          </div>
        </div>
      )}
      {apps.length > 0 && (
        <ul className="list-plain">
          {apps.map((a) => (
            <li key={a.id} className="stack" style={{ gap: 6 }}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <span>
                  <strong>{a.name}</strong>
                  {a.github_repo && <span className="small muted"> · {a.github_repo}</span>}
                  <span className="small muted"> · {a.active_keys} active key{a.active_keys === 1 ? "" : "s"}</span>
                </span>
                <span className="row" style={{ gap: 6 }}>
                  <button className="btn btn-small" onClick={() => createKey(a.id)}>New key</button>
                  <button className="btn btn-small" onClick={() => setKeysFor(keysFor === a.id ? null : a.id)}>{keysFor === a.id ? "Hide keys" : "Keys"}</button>
                </span>
              </div>
              {keysFor === a.id && <AppKeys appId={a.id} onChange={onChange} />}
            </li>
          ))}
        </ul>
      )}
      <form className="row" onSubmit={add} style={{ alignItems: "flex-end" }}>
        <div className="field" style={{ flex: 1, minWidth: 140 }}>
          <label htmlFor="a-name">App name</label>
          <input id="a-name" type="text" required maxLength={120} placeholder="Pulse" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1, minWidth: 160 }}>
          <label htmlFor="a-repo">GitHub repo (optional)</label>
          <input id="a-repo" type="text" placeholder="Departure-Labs/repo" value={repo} onChange={(e) => setRepo(e.target.value)} />
        </div>
        <button className="btn">Add app</button>
      </form>
      {error && <p className="error-text" role="alert">{error}</p>}
    </>
  );
}

function AppKeys({ appId, onChange }: { appId: string; onChange: () => void }) {
  const { data, reload } = useApi<{ keys: Array<{ id: string; prefix: string; created_by: string; created_at: string; last_used_at: string | null; revoked_at: string | null }> }>(`/staff/apps/${appId}/keys`);
  if (!data) return <Loading />;
  if (data.keys.length === 0) return <p className="small muted">No keys yet.</p>;
  return (
    <ul className="list-plain small">
      {data.keys.map((k) => (
        <li key={k.id} className="row" style={{ justifyContent: "space-between" }}>
          <span>
            <span className="mono">dlp_{k.prefix}…</span> · created {formatDate(k.created_at)} by {k.created_by} · {k.last_used_at ? `used ${timeAgo(k.last_used_at)}` : "never used"}
            {k.revoked_at && <strong> · revoked</strong>}
          </span>
          {!k.revoked_at && (
            <button className="btn btn-small btn-danger" onClick={() => api(`/staff/keys/${k.id}`, { method: "DELETE" }).then(() => (reload(), onChange()))}>
              Revoke
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
